import { useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../state/AuthContext";
import { DEMO_EMPLOYEE } from "../lib/roles";
import {
  SEED_POLICIES,
  SEED_TIMESHEET_LINES,
  SEED_TIMESHEETS,
  employmentStatusFor,
} from "../data/timekeepingSeed";
import {
  EMPLOYMENT_STATUS_HELP,
  formatPeriodLabel,
  requiredHoursForPeriod,
  policyForDate,
  sheetTotal,
  type TimesheetHeader,
  type TimesheetLine,
} from "../lib/timekeeping";
import { StatusChip } from "../components/timekeeping/TimekeepingWidgets";
import { PageHeader } from "../components/RequestRouting";

type LdrRow = {
  employee: string;
  employee_id: string;
  job: string;
  hours: number;
  dollars: number;
  timesheet_id: string;
  status: string;
};

type ExceptionRow = {
  id: string;
  kind: "hours" | "payroll" | "gl" | "unapproved";
  message: string;
  severity: "block" | "warn";
};

function cloneSheets(): TimesheetHeader[] {
  return SEED_TIMESHEETS.map((s) => ({ ...s }));
}

function cloneLines(): TimesheetLine[] {
  return SEED_TIMESHEET_LINES.map((l) => ({ ...l, hours: { ...l.hours } }));
}

export function LaborDistributionPage() {
  const { moduleIds, homePath, roleIds } = useAuth();
  const [sheets, setSheets] = useState(cloneSheets);
  const [lines] = useState(cloneLines);
  const [postedIds, setPostedIds] = useState<string[]>([]);
  const [exportBlocked, setExportBlocked] = useState<string[]>([]);
  const [flash, setFlash] = useState<string | null>(null);

  const sodWarning =
    roleIds.includes("ROLE-SUPERVISOR") &&
    (roleIds.includes("ROLE-PAYROLL") || roleIds.includes("ROLE-HR-PAYROLL"));

  if (!moduleIds.includes("labor")) return <Navigate to={homePath} replace />;

  const approved = sheets.filter((s) => s.status === "Approved" || postedIds.includes(s.id));
  const unapproved = sheets.filter(
    (s) => s.status === "Submitted" || s.status === "Draft" || s.status === "Correction requested",
  );

  const ldr: LdrRow[] = useMemo(() => {
    const rows: LdrRow[] = [];
    for (const s of sheets.filter((x) => x.status === "Approved" || postedIds.includes(x.id))) {
      const slines = lines.filter((l) => l.timesheet_id === s.id);
      const byCode = new Map<string, number>();
      for (const l of slines) {
        const h = Object.values(l.hours).reduce((a, b) => a + (Number(b) || 0), 0);
        byCode.set(l.charge_code, (byCode.get(l.charge_code) ?? 0) + h);
      }
      for (const [job, hours] of byCode) {
        rows.push({
          employee: s.employee,
          employee_id: s.employee_id,
          job,
          hours,
          dollars: Math.round(hours * 85 * 100) / 100,
          timesheet_id: s.id,
          status: postedIds.includes(s.id) ? "Posted" : s.status,
        });
      }
    }
    return rows;
  }, [sheets, lines, postedIds]);

  const exceptions: ExceptionRow[] = useMemo(() => {
    const out: ExceptionRow[] = [];
    for (const s of unapproved) {
      out.push({
        id: `u-${s.id}`,
        kind: "unapproved",
        severity: "block",
        message: `${s.employee} — ${formatPeriodLabel(s.period_start, s.period_end)} is ${s.status} (blocked from payroll export)`,
      });
    }
    // Demo synthetic payroll variance on one posted row
    if (ldr.some((r) => r.job === "IND-G&A")) {
      out.push({
        id: "p-gna",
        kind: "payroll",
        severity: "warn",
        message: "IND-G&A dollars differ from payroll export stub by $12.50 (demo variance)",
      });
    }
    out.push({
      id: "gl-ok",
      kind: "gl",
      severity: "warn",
      message: "GL labor control account reconcile pending period close (demo stub)",
    });
    return out;
  }, [unapproved, ldr]);

  function tryPayrollExport() {
    const blocked = sheets
      .filter((s) => s.status !== "Approved" && !postedIds.includes(s.id))
      .filter((s) => s.status !== "Rejected")
      .map((s) => s.id);
    setExportBlocked(blocked);
    if (blocked.length) {
      setFlash(`Export blocked — ${blocked.length} timesheet(s) not approved (DEC-TIME-006).`);
      return;
    }
    setFlash("Payroll export stub succeeded for approved timesheets.");
  }

  function postApproved() {
    const ids = sheets.filter((s) => s.status === "Approved").map((s) => s.id);
    if (!ids.length) {
      setFlash("No approved timesheets ready to post.");
      return;
    }
    setPostedIds((prev) => [...new Set([...prev, ...ids])]);
    setSheets((prev) =>
      prev.map((s) => (ids.includes(s.id) ? { ...s, status: "Approved" as const } : s)),
    );
    setFlash(`Posted ${ids.length} timesheet(s) after labor distribution (TR-TIME-005).`);
  }

  const retentionNote =
    "Retention class timesheet / labor_distribution — FAR 4.7 theme (≥ 3 years after final tax/payment period; legal hold supported).";

  return (
    <div className="space-y-4">
      <PageHeader
        title="Labor distribution"
        subtitle="Cost approved time, reconcile hours/payroll/GL, then post timesheets."
      />

      {sodWarning && (
        <div className="border border-gold bg-gold/10 px-3 py-2 text-sm text-navy">
          SoD warning: this demo role preview combines timesheet approval with payroll hats —
          non-compliant for production (DEC-TIME-006).
        </div>
      )}

      {flash && (
        <div className="border border-teal/40 bg-teal/10 px-3 py-2 text-sm text-navy">{flash}</div>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="px-3 py-2 text-sm bg-navy text-white"
          onClick={tryPayrollExport}
        >
          Run payroll export
        </button>
        <button
          type="button"
          className="px-3 py-2 text-sm bg-teal text-white"
          onClick={postApproved}
        >
          Post approved → Posted
        </button>
      </div>

      <section className="bg-white border border-black/10 p-4 space-y-2">
        <h2 className="font-semibold text-navy">Labor Distribution Report</h2>
        <p className="text-xs text-slate">
          Hours from approved/posted timesheets · dollars = hours × demo rate $85 ·{" "}
          {retentionNote}
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="bg-mist text-left">
                <th className="px-2 py-2">Employee</th>
                <th className="px-2 py-2">Job / code</th>
                <th className="px-2 py-2 text-right">Hours</th>
                <th className="px-2 py-2 text-right">Dollars</th>
                <th className="px-2 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {ldr.map((r, i) => (
                <tr key={`${r.timesheet_id}-${r.job}-${i}`} className="border-t border-black/5">
                  <td className="px-2 py-1.5">{r.employee}</td>
                  <td className="px-2 py-1.5 font-mono text-xs">{r.job}</td>
                  <td className="px-2 py-1.5 text-right">{r.hours}</td>
                  <td className="px-2 py-1.5 text-right">${r.dollars.toFixed(2)}</td>
                  <td className="px-2 py-1.5">
                    <StatusChip status={r.status} />
                    {postedIds.includes(r.timesheet_id) && r.status === "Approved" ? (
                      <span className="ml-1 text-xs text-teal">Posted</span>
                    ) : null}
                  </td>
                </tr>
              ))}
              {ldr.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-2 py-6 text-center text-slate">
                    No approved timesheets in demo seed for LDR yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="bg-white border border-black/10 p-4 space-y-2">
        <h2 className="font-semibold text-navy">Reconcile exceptions</h2>
        <ul className="space-y-2 text-sm">
          {exceptions.map((e) => (
            <li
              key={e.id}
              className={`px-3 py-2 border ${
                e.severity === "block" ? "border-red-300 bg-red-50" : "border-gold/40 bg-gold/10"
              }`}
            >
              <span className="text-xs font-medium uppercase tracking-wide text-slate">
                {e.kind}
              </span>
              <div className="text-navy mt-0.5">{e.message}</div>
            </li>
          ))}
        </ul>
        {exportBlocked.length > 0 && (
          <p className="text-xs text-red-700">
            Last export blocked ids: {exportBlocked.join(", ")}
          </p>
        )}
      </section>

      <section className="bg-white border border-black/10 p-4 space-y-1 text-sm text-slate">
        <h2 className="font-semibold text-navy">Demo employee floor check</h2>
        <p>
          {DEMO_EMPLOYEE.name} status{" "}
          <strong className="text-navy">{employmentStatusFor(DEMO_EMPLOYEE.id)}</strong> (
          {EMPLOYMENT_STATUS_HELP[employmentStatusFor(DEMO_EMPLOYEE.id)].label})
          {(() => {
            const pol = policyForDate(SEED_POLICIES, "co-hfs", "2026-07-27");
            const req = pol
              ? requiredHoursForPeriod(pol, employmentStatusFor(DEMO_EMPLOYEE.id))
              : null;
            const mine = sheets.find(
              (s) => s.employee_id === DEMO_EMPLOYEE.id && s.period_start === "2026-07-27",
            );
            const total = mine
              ? sheetTotal(lines.filter((l) => l.timesheet_id === mine.id))
              : 0;
            return req != null ? ` · sample period total ${total} / min ${req}` : "";
          })()}
        </p>
        <p className="text-xs">Approved ready: {approved.length} · Unapproved blockers: {unapproved.length}</p>
      </section>
    </div>
  );
}
